(() => {
  if (customElements.get('primo-category-object')) return;
  const cache = new Map();
  const resolution = 256;
  let renderer;
  let moving;
  const decode = (value, Type) => new Type(Uint8Array.from(atob(value), c => c.charCodeAt(0)).buffer);
  const load = url => {
    if (!cache.has(url)) {
      const task = fetch(url).then(response => {
        if (!response.ok) throw new Error('Category model unavailable');
        return response.json();
      }).then(async data => {
        if (data.version !== 1) throw new Error('Unknown model format');
        const image = new Image();
        image.src = data.texture;
        await image.decode();
        return { vertices: decode(data.vertices, Float32Array), indices: decode(data.indices, Uint16Array), image, view: data.view || [1.15, .30] };
      }).catch(error => { cache.delete(url); throw error; });
      cache.set(url, task);
    }
    return cache.get(url);
  };
  function createRenderer() {
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = resolution;
    canvas.setAttribute('aria-hidden', 'true');
    const gl = canvas.getContext('webgl', { alpha: true, antialias: true, powerPreference: 'low-power' });
    if (!gl) throw new Error('WebGL unavailable');
    const program = gl.createProgram();
    const shader = (type, source) => {
      const result = gl.createShader(type);
      gl.shaderSource(result, source);
      gl.compileShader(result);
      if (!gl.getShaderParameter(result, gl.COMPILE_STATUS)) throw new Error('Shader unavailable');
      gl.attachShader(program, result);
      gl.deleteShader(result);
    };
    shader(gl.VERTEX_SHADER, `
      attribute vec3 position;
      attribute vec3 normal;
      attribute vec2 uv;
      uniform vec2 angle;
      uniform vec2 motion;
      varying vec2 textureUV;
      varying float light;
      vec3 rotate(vec3 p) {
        float c=cos(angle.x), s=sin(angle.x), cp=cos(angle.y), sp=sin(angle.y);
        vec3 a=vec3(c*p.x+s*p.z,p.y,-s*p.x+c*p.z);
        vec3 b=vec3(a.x,cp*a.y-sp*a.z,sp*a.y+cp*a.z);
        float cr=cos(motion.x), sr=sin(motion.x);
        return vec3(cr*b.x-sr*b.y,sr*b.x+cr*b.y,b.z);
      }
      void main() {
        vec3 p=rotate(position);
        gl_Position=vec4(p.x,p.y+motion.y,-p.z*.2,1.0);
        textureUV=uv;
        light=.88+.12*max(dot(normalize(rotate(normal)),normalize(vec3(-.4,.6,1.0))),0.0);
      }
    `);
    shader(gl.FRAGMENT_SHADER, `
      precision mediump float;
      uniform sampler2D surface;
      varying vec2 textureUV;
      varying float light;
      void main() { vec4 c=texture2D(surface,textureUV); gl_FragColor=vec4(c.rgb*light,c.a); }
    `);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error('Renderer unavailable');
    gl.useProgram(program);
    gl.enable(gl.DEPTH_TEST);
    gl.clearColor(0, 0, 0, 0);
    const vertices = gl.createBuffer(), indices = gl.createBuffer(), texture = gl.createTexture();
    const angle = gl.getUniformLocation(program, 'angle');
    const motion = gl.getUniformLocation(program, 'motion');
    let count = 0, current;
    canvas.addEventListener('webglcontextlost', event => {
      event.preventDefault();
      moving?.stop();
      canvas.remove();
      renderer = null;
    });
    return {
      canvas,
      set(model) {
        if (current === model) return;
        current = model;
        gl.bindBuffer(gl.ARRAY_BUFFER, vertices);
        gl.bufferData(gl.ARRAY_BUFFER, model.vertices, gl.STATIC_DRAW);
        for (const [name, size, offset] of [['position',3,0],['normal',3,12],['uv',2,24]]) {
          const location = gl.getAttribLocation(program, name);
          gl.enableVertexAttribArray(location);
          gl.vertexAttribPointer(location, size, gl.FLOAT, false, 32, offset);
        }
        gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, indices);
        gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, model.indices, gl.STATIC_DRAW);
        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.texImage2D(gl.TEXTURE_2D,0,gl.RGBA,gl.RGBA,gl.UNSIGNED_BYTE,model.image);
        gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MIN_FILTER,gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_MAG_FILTER,gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_WRAP_S,gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D,gl.TEXTURE_WRAP_T,gl.CLAMP_TO_EDGE);
        count = model.indices.length;
      },
      draw(time, tilt) {
        gl.viewport(0,0,resolution,resolution);
        gl.clear(gl.COLOR_BUFFER_BIT|gl.DEPTH_BUFFER_BIT);
        gl.uniform2f(angle, current.view[0] + Math.sin(time)*.38, current.view[1]+Math.sin(time*.7)*.045);
        gl.uniform2f(motion, tilt ? Math.sin(time)*.35 : 0, tilt ? Math.abs(Math.sin(time))*.04 : 0);
        gl.drawElements(gl.TRIANGLES,count,gl.UNSIGNED_SHORT,0);
      }
    };
  }
  customElements.define('primo-category-object', class extends HTMLElement {
    connectedCallback() {
      this.events?.abort();
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.link = this.closest('a');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.pointer = matchMedia('(hover: hover) and (pointer: fine)');
      this.link.addEventListener('pointerenter', () => this.start(), options);
      this.link.addEventListener('pointerleave', () => this.stop(), options);
      this.link.addEventListener('focus', () => this.start(), options);
      this.link.addEventListener('blur', () => this.stop(), options);
      this.motion.addEventListener('change', () => this.stop(), options);
      this.pointer.addEventListener('change', () => this.stop(), options);
      document.addEventListener('visibilitychange', () => { if (document.hidden) this.stop(); }, options);
      this.observer?.disconnect();
      this.observer = new IntersectionObserver(entries => {
        if (!entries[0].isIntersecting) this.stop();
      });
      this.observer.observe(this);
    }
    async prepare() {
      if (this.model || !this.isConnected) return;
      if (this.loading) return this.loading;
      this.loading = (async () => {
        try {
          const model = await load(this.dataset.model);
          if (!this.isConnected) return;
          this.canvas ||= document.createElement('canvas');
          this.canvas.width = this.canvas.height = resolution;
          this.canvas.setAttribute('aria-hidden', 'true');
          this.context ||= this.canvas.getContext('2d');
          if (!this.context) return;
          this.model = model;
          this.render(0);
          this.append(this.canvas);
          this.classList.add('is-ready');
        } catch (_) { this.model = null; }
      })();
      await this.loading;
      this.loading = null;
    }
    render(time) {
      renderer ||= createRenderer();
      renderer.set(this.model);
      renderer.draw(time, this.dataset.motion === 'tilt');
      this.context.clearRect(0, 0, resolution, resolution);
      this.context.drawImage(renderer.canvas, 0, 0);
    }
    async start() {
      if (!this.isConnected || this.motion.matches || !this.pointer.matches || document.hidden || this.active) return;
      moving?.stop();
      moving = this;
      this.active = true;
      const token = this.token = (this.token || 0) + 1;
      await this.prepare();
      if (!this.model) { this.stop(); return; }
      if (!this.active || token !== this.token || !this.isConnected) return;
      this.dataset.moving = 'true';
      let start, last = 0;
      const tick = now => {
        if (!this.active) return;
        start ??= now;
        try {
          if (now-last >= 32) { this.render((now-start)/450); last=now; }
          this.frame=requestAnimationFrame(tick);
        } catch (_) { this.stop(); }
      };
      this.frame=requestAnimationFrame(tick);
    }
    stop() {
      this.active = false;
      this.token = (this.token || 0) + 1;
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      delete this.dataset.moving;
      if (moving === this) moving = null;
      if (this.model && this.isConnected) {
        try { this.render(0); } catch (_) { /* Retain the last successful frame. */ }
      }
    }
    disconnectedCallback() {
      this.stop();
      this.events?.abort();
      this.observer?.disconnect();
    }
  });
})();
